<script lang="ts">
    import AlternativesImportPage from '@Alternatives/Alternatives/Pages/AlternativesImportPage'
    export default AlternativesImportPage
</script>

<template>
    <div>

        <AttributeEditorDialog :viewModel="attributeEditorDialog" 
                               @attribute-created="loadAttribute" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Import</h1>
                </div>
                <div class="col-lg-6 text-right">
                    <div v-if="!Processing">
                        <button v-if="ImportStep === 'mappings'" type="button" @click="cancelImport" class="btn btn-outline-secondary mr-1" style="padding: 0.7em 2em;">Back</button>
                        <button v-if="NextToAlternativesAttributesMappingsButtonVisible" type="button" @click="ImportStep = 'mappings'" class="btn btn-outline-secondary" style="padding: 0.7em 2em;">Next</button>
                        <button v-if="ImportStep === 'mappings'" type="button" @click="handleImportAlternativesFromSpreadsheetCommand" class="btn btn-primary" style="padding: 0.7em 2em;">Import</button>
                    </div>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 60px;" />

        <div class="container">

            <div v-if="ImportStep === 'source'">
                <div class="card-deck">
                    <div class="card bg-light mb-3">
                        <div class="card-body">
                            <h5 class="card-title">Import tips</h5>
                            <p class="card-text">
                                The best way to import your data is to use our
                                <a @click="handleDownloadEmptyAlternativesSpreadsheetCommand" class="text-primary">sample import file</a>
                            </p>
                            <p class="card-text">
                                If you want to use your custom file, here are our recommendations about its structure:
                            </p>
                            <ul>
                                <li>Use columns as attributes and rows as data</li>
                                <li>Include "Name" column to indicate unique name</li>
                                <li>Optionally include "Description" column to indicate description to be used</li>
                                <li>Avoid merged cells, images, charts</li>
                            </ul>
                        </div>
                    </div>

                    <div class="card text-dark bg-light mb-3">
                        <div class="card-body">
                            <h5 class="card-title text-danger">DANGER: YOUR DATA MAY BE DELETED</h5>
                            <p class="card-text">
                                When importing data from a spreadsheet, you can choose whether or not to import data for existing projects/alternatives.
                                When you do import these data, any blanks in your spreadsheet will effectively "delete" any data already in TransparentChoice.
                            </p>
                        </div>
                        <div class="card-footer">
                            <div class="form-check">
                                <input class="form-check-input" type="checkbox" v-model="UpdateExisting">
                                <label class="form-check-label">
                                    Check this box to update data for existing alternatives. Leave it unchecked if you only want to add new alternatives from your spreadsheet.
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <br />

                <h4>Choose file to import (.xls or .xlsx)</h4>
                <FileUploader @selected="file=$event" @unselected="file=null" @uploaded="loadMappings" />

                <div v-if="rm && rm.HasErrors">
                    <strong v-for="err in SpreadsheetErrors" class="fg-color-red">{{err}}</strong><br />
                </div>

            </div>

            <div v-if="ImportStep === 'mappings'">
                <h4>Map columns to attributes</h4>

                <table class="table table-bordered">
                    <thead>
                        <tr>
                            <th scope="col">Column name</th>
                            <th scope="col">Attribute name</th>
                            <th scope="col"></th>

                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="m in rm.Mappings" :key="m.ColumnName" :class="{'table-warning': m.PropertyName === 'None'}">
                            <td>{{m.ColumnName}}</td>
                            <td>
                                <select class="Value form-control" v-model="m.PropertyName">
                                    <option value="None">[Do not import]</option>
                                    <option value="Name">Name</option>
                                    <option value="Description">Description</option>
                                    <option value="Status">Status</option>
                                    <option v-for="attr in AttributesOrdered" :value="attr.Name" :key="attr.Id">{{ attr.Name }}</option>
                                </select>
                                <div class="error SpreadsheetError">{{m.Error}}</div>
                            </td>
                            <td>
                                <button @click="showAttributeCreator(m)" type="button" class="btn btn-outline-secondary">Create attribute</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <br />
            </div>

            <div v-if="ImportStep === 'summary'" style="height: 100%;">
                <h4>{{ ImportCompleted ? 'Import completed' : 'Import in progress...' }}  </h4>

                <div v-if="importResult">
                    <div class="row">
                        <div class="col-sm-3">
                            <p><strong>New:</strong></p>
                            <h4>{{importResult.Created.length}}</h4>
                        </div>

                        <div class="col-sm-3">
                            <p><strong>Existing:</strong></p>
                            <h4> {{importResult.Updated.length + importResult.NotChanged.length}}</h4>
                        </div>

                        <div class="col-sm-3">
                            <p><strong>Processed:</strong></p>
                            <h4>{{importResult.Created.length + importResult.Updated.length + importResult.NotChanged.length}}</h4>
                        </div>

                        <div class="col-sm-3">
                            <p><strong>Failed:</strong></p>
                            <h4>{{importResult.FailedToCreate.length + importResult.FailedToUpdate.length}}</h4>
                        </div>
                    </div>
                    <br />
                    <div style="overflow-x: hidden; font-family: 'Consolas', 'Courier New', monospace; line-height: 1.5; font-size: 16px;">
                        <div v-if="importResult.FailedToCreate.length">
                            <strong class='fg-color-red'>Failed to create ({{importResult.FailedToCreate.length}})</strong>
                            <div v-for="a in importResult.FailedToCreate" :key="`create-${a}`" style="margin-left: 20px;">
                                <span class='fg-color-red'>{{a}}</span> <span>{{getError(a)}}</span><br />
                            </div>
                        </div>

                        <div v-if="importResult.FailedToUpdate.length">
                            <strong class='fg-color-red'>Failed to update ({{importResult.FailedToUpdate.length}})</strong>
                            <div v-for="a in importResult.FailedToUpdate" :key="`update-${a}`" style="margin-left: 20px;">
                                <span class='fg-color-red'>{{a}}</span> <span>{{getError(a)}}</span><br />
                            </div>
                        </div>

                        <div v-if="importResult.Created.length">
                            <strong class='fg-color-green'>Created ({{importResult.Created.length}})</strong>
                            <div v-for="a in importResult.Created" :key="`created-${a}`" style="margin-left: 20px;">
                                <span class='fg-color-green'>{{a}}</span><br />
                            </div>
                        </div>

                        <div v-if="importResult.Updated.length">
                            <strong class='fg-color-green'>Updated ({{importResult.Updated.length}})</strong>
                            <div v-for="a in importResult.Updated" :key="`updated-${a}`" style="margin-left: 20px;">
                                <span class='fg-color-green'>{{a}}</span><br />
                            </div>
                        </div>

                        <div v-if="importResult.NotChanged.length">
                            <strong>No changes detected ({{importResult.NotChanged.length}})</strong>
                            <div v-for="a in importResult.NotChanged" :key="`notchanged-${a}`" style="margin-left: 20px;">
                                <span>{{a}}</span><br />
                            </div>
                        </div>

                    </div>
                </div>
            </div>



        </div>
    </div>
</template>

<style scoped>
    table.AlternativesPreview {
        margin: 2px;
        border-collapse: collapse;
        width: 100%;
    }

        table.AlternativesPreview td {
            position: relative;
            border: solid 1px lightgray;
            padding: 3px;
            max-width: 200px;
        }

            table.AlternativesPreview td:not(.Active):hover {
                outline: solid 2px gray;
            }

            table.AlternativesPreview td.Error {
                background-color: rgba(252, 66, 55, 0.5);
                font-weight: bold;
            }

        table.AlternativesPreview div.ActiveCellPlaceholder {
            position: absolute;
        }

        table.AlternativesPreview div.ActiveCell {
            position: relative;
            top: 0px;
            left: 0px;
            padding: 5px;
            border: solid 2px gray;
            background: lightgray;
            z-index: 100;
            border-radius: 3px;
        }
</style>


